import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { API_URL, refreshAccessToken, getRefreshFailure, refreshGotNoAnswer, getAccessToken, setAccessToken, clearAccessToken, setViewAsRequestHeaders, XHR_HEADER } from '../utils/api';
import { forgetOrderCopies } from '../utils/offlineOrders';
import { forgetScribeDrafts, clearDraftsStartingWith } from '../utils/localDraft';
import useViewAsPreview from '../hooks/useViewAsPreview';
import { readPref, writePref, readJsonPref, removePref } from '../utils/safeStorage';

// Cross-tab logout signal. localStorage 'storage' events no longer fire for
// auth state (tokens are not in localStorage), so we use BroadcastChannel.
const AUTH_CHANNEL = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('chariot-auth') : null;

// Bounded /auth/me fetch — every call to /auth/me must have a timeout. Without
// one, a hung connection (iPad on bad WiFi, Cloud Run cold-start, marina LTE)
// keeps the AuthProvider's `loading` flag stuck at true and the user sees an
// infinite spinner. AbortError propagates to the surrounding try/catch where
// the caller already treats network failures as a no-op.
const AUTH_TIMEOUT_MS = 30000;
const fetchAuthMe = (token) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), AUTH_TIMEOUT_MS);
  return fetch(`${API_URL}/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
    signal: controller.signal,
  }).finally(() => clearTimeout(timer));
};

const AuthContext = createContext(null);

// A technician who works orders on a tablet (the work view) can open Chariot
// with no signal — after a reload, or iPad Safari dropping the tab — on what
// the device kept: their name and permissions here (never a token: the
// session is the httpOnly cookie), their orders (utils/offlineOrders.js) and
// their saves (utils/offlineOutbox.js). Renewed at every sign-in, no older
// than the sign-in itself lasts (7 days), gone at sign-out.
const OFFLINE_ME = 'chariot-offline-me';
const OFFLINE_ME_MAX_MS = 7 * 24 * 60 * 60 * 1000;
const noSignal = () => refreshGotNoAnswer() || (typeof navigator !== 'undefined' && navigator.onLine === false);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  // accessible_locations from /auth/me — exposed here so LocationContext can
  // consume it instead of firing its own duplicate /auth/me on every mount.
  const [accessibleLocations, setAccessibleLocations] = useState([]);
  // Permission catalog — fetched once on login, used to compute role defaults
  // when admin previews as a role (viewAsRole without viewAsUserId).
  const [permissionCatalog, setPermissionCatalog] = useState([]);
  // Parent-permission map (permissions-catalog.js PERMISSION_DEPENDENCIES) —
  // needed so role preview gates children the same way the server does.
  const [permissionDependencies, setPermissionDependencies] = useState({});
  const [loading, setLoading] = useState(true);
  const [authRetrying, setAuthRetrying] = useState(false); // server unreachable at boot
  const [offlineSession, setOfflineSession] = useState(false); // opened with no signal on what this device kept
  const [error, setError] = useState(null);
  const refreshTimerRef = useRef(null);

  const clearSession = useCallback(() => {
    clearAccessToken();
    setUser(null);
    setPermissions({});
    setAccessibleLocations([]);
    setViewAsRequestHeaders(null);
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // Use the shared mutex-protected refresh from api.js to prevent race conditions.
  // Multiple callers (timer, visibility check, fetchWithAuth 401 handler) all
  // funnel through the same promise, so only one refresh request hits the server.
  const refreshToken = useCallback(async () => {
    const refreshed = await refreshAccessToken();
    if (!refreshed) return false;

    // Fetch user data with the new token
    try {
      const token = getAccessToken();
      const userResponse = await fetchAuthMe(token);
      if (userResponse.ok) {
        const userData = await userResponse.json();
        setUser(userData.user);
        setPermissions(userData.permissions || {});
        setAccessibleLocations(userData.accessible_locations || []);
        return true;
      }
    } catch (err) {
      console.error('Failed to fetch user after refresh:', err);
    }
    return false;
  }, []);

  // Start auto-refresh timer (refresh every 3.5h, 30 min before 4h expiry)
  const startRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
    }
    refreshTimerRef.current = setInterval(async () => {
      if (getAccessToken()) {
        await refreshToken();
        // Don't clear session on refresh failure — the user may still have
        // a valid access token for a while, and the next API call or
        // visibility check will retry the refresh.
      }
    }, 3.5 * 60 * 60 * 1000); // 3.5 hours
  }, [refreshToken]);

  // Check for existing session on mount.
  // Access token is in-memory only, so on every page load we have to ask the
  // refresh endpoint (which reads the httpOnly cookie) for a fresh access token.
  useEffect(() => {
    let cancelled = false;
    // The work view's kept session (OFFLINE_ME): true when it opened.
    const openKeptSession = () => {
      const kept = readJsonPref(OFFLINE_ME, null);
      if (!kept?.user?.id || !(Date.now() - Number(kept.savedAt) < OFFLINE_ME_MAX_MS)) return false;
      setUser(kept.user);
      setPermissions(kept.permissions || {});
      setAccessibleLocations(kept.accessible_locations || []);
      setOfflineSession(true);
      return true;
    };
    const checkAuth = async () => {
      // Keep retrying while the SERVER is the problem (cold start, stall,
      // deploy) — a valid refresh cookie must never turn into a logout just
      // because Cloud Run took 20 seconds to answer. Only a real 401 ("your
      // session is over") falls through to the login page. With no answer at
      // all, a technician's kept session opens meanwhile.
      let delay = 2000;
      let offline = false;
      for (;;) {
        try {
          const refreshed = await refreshToken();
          if (refreshed) {
            startRefreshTimer();
            break;
          }
          if (getRefreshFailure() !== 'unavailable') { // truly logged out
            removePref(OFFLINE_ME);
            break;
          }
          if (!offline && noSignal() && openKeptSession()) {
            offline = true;
            setAuthRetrying(false);
            setLoading(false);
          }
        } catch (err) {
          console.error('Auth check failed:', err);
        }
        if (cancelled) return;
        if (!offline) setAuthRetrying(true);
        const wait = delay;
        await new Promise((ok) => { setTimeout(ok, wait); });
        if (cancelled) return;
        delay = Math.min(delay * 2, 15000);
      }
      if (cancelled) return;
      if (offline) {
        setOfflineSession(false);
        // The session ended while there was no signal: the sign-in page.
        if (!getAccessToken()) clearSession();
      }
      setAuthRetrying(false);
      setLoading(false);
    };

    checkAuth();

    return () => {
      cancelled = true;
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The work view's kept session (OFFLINE_ME), renewed with each real sign-in.
  useEffect(() => {
    if (!user || offlineSession) return;
    if (permissions['orders.work'] === true && permissions['orders.edit'] !== true) {
      writePref(OFFLINE_ME, JSON.stringify({ user, permissions, accessible_locations: accessibleLocations, savedAt: Date.now() }));
    } else {
      removePref(OFFLINE_ME);
    }
  }, [user, permissions, accessibleLocations, offlineSession]);

  // Fetch the permission catalog when user logs in (used for role-preview computation)
  useEffect(() => {
    if (!user) {
      setPermissionCatalog([]);
      setPermissionDependencies({});
      return;
    }
    const token = getAccessToken();
    if (!token) return;
    fetch(`${API_URL}/permissions/catalog`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.catalog) setPermissionCatalog(data.catalog);
        if (data?.dependencies) setPermissionDependencies(data.dependencies);
      })
      .catch(() => { /* non-fatal; catalog only needed for role-preview accuracy */ });
    // (A kept session opened with no signal has no token: again once it signs in.)
  }, [user?.id, offlineSession]);

  // Cross-tab logout via BroadcastChannel
  useEffect(() => {
    if (!AUTH_CHANNEL) return undefined;
    const handler = (e) => {
      if (e.data === 'logout') {
        clearSession();
      }
    };
    AUTH_CHANNEL.addEventListener('message', handler);
    return () => AUTH_CHANNEL.removeEventListener('message', handler);
  }, [clearSession]);

  // Visibility check: verify session when tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && user) {
        // Check inactivity timeout (6 hours)
        const lastActivity = readPref('chariot-last-activity');
        if (lastActivity) {
          const elapsed = Date.now() - parseInt(lastActivity, 10);
          if (elapsed > 6 * 60 * 60 * 1000) {
            clearSession();
            return;
          }
        }

        const token = getAccessToken();
        if (!token) {
          // Try a silent refresh from the cookie before giving up
          const refreshed = await refreshToken();
          if (!refreshed) clearSession();
          return;
        }

        try {
          const response = await fetchAuthMe(token);
          if (!response.ok) {
            // Token may be expired — try to refresh silently.
            // Don't log out on failure; the next API call will also
            // attempt refresh via fetchWithAuth's 401 handler.
            await refreshToken();
          }
        } catch (err) {
          // Network error / timeout — don't log out, just skip
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, refreshToken, clearSession]);

  // Track user activity for 6-hour inactivity timeout
  useEffect(() => {
    if (!user) return;

    const updateActivity = () => {
      writePref('chariot-last-activity', Date.now().toString());
    };

    // Set initial activity timestamp
    updateActivity();

    // Throttle activity updates to once per minute
    let lastUpdate = Date.now();
    const handleActivity = () => {
      if (Date.now() - lastUpdate > 60000) {
        lastUpdate = Date.now();
        updateActivity();
      }
    };

    window.addEventListener('mousedown', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('scroll', handleActivity, true);

    // Check inactivity every 15 minutes
    const inactivityCheck = setInterval(() => {
      const lastActivity = readPref('chariot-last-activity');
      if (lastActivity) {
        const elapsed = Date.now() - parseInt(lastActivity, 10);
        if (elapsed > 6 * 60 * 60 * 1000) {
          clearSession();
        }
      }
    }, 15 * 60 * 1000);

    return () => {
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('scroll', handleActivity, true);
      clearInterval(inactivityCheck);
    };
  }, [user, clearSession]);

  const login = async (email, password) => {
    setError(null);

    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...XHR_HEADER, // login sets the session cookie: same CSRF guard as /refresh
        },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Login failed', code: data.code };
      }

      // Check if 2FA is required
      if (data.code === '2FA_REQUIRED') {
        return {
          success: false,
          requires2FA: true,
          tempToken: data.tempToken,
          message: data.message
        };
      }

      // Refresh token is stored as an httpOnly cookie by the server.
      // Access token lives only in memory.
      setAccessToken(data.accessToken);

      setUser(data.user);
      // Fetch permissions + accessible_locations from /auth/me (login endpoint
      // returns user but not computed permissions or locations).
      try {
        const meResponse = await fetchAuthMe(data.accessToken);
        if (meResponse.ok) {
          const meData = await meResponse.json();
          setPermissions(meData.permissions || {});
          setAccessibleLocations(meData.accessible_locations || []);
        }
      } catch (err) {
        console.error('Failed to fetch permissions after login:', err);
      }
      startRefreshTimer();
      return { success: true };

    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const verify2FA = async (code, tempToken, trustDevice = true) => {
    setError(null);

    try {
      const response = await fetch(`${API_URL}/auth/verify-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...XHR_HEADER,
        },
        credentials: 'include',
        body: JSON.stringify({ code, tempToken, trustDevice })
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error, attemptsRemaining: data.attemptsRemaining };
      }

      // Refresh token is stored as an httpOnly cookie by the server.
      // Access token lives only in memory.
      setAccessToken(data.accessToken);

      setUser(data.user);
      // Fetch permissions + accessible_locations from /auth/me.
      try {
        const meResponse = await fetchAuthMe(data.accessToken);
        if (meResponse.ok) {
          const meData = await meResponse.json();
          setPermissions(meData.permissions || {});
          setAccessibleLocations(meData.accessible_locations || []);
        }
      } catch (err) {
        console.error('Failed to fetch permissions after 2FA:', err);
      }
      startRefreshTimer();
      return { success: true };

    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const resend2FA = async (tempToken) => {
    try {
      const response = await fetch(`${API_URL}/auth/resend-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ tempToken })
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error };
      }

      return { success: true, message: data.message };

    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const register = async (userData) => {
    setError(null);

    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      // Registration no longer returns tokens - user must verify email first
      return { success: true, message: data.message };

    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const logout = async () => {
    const token = getAccessToken();

    // Call logout endpoint to invalidate refresh token + clear cookie
    if (token) {
      try {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include'
        });
      } catch (err) {
        console.error('Logout API call failed:', err);
      }
    }

    if (AUTH_CHANNEL) AUTH_CHANNEL.postMessage('logout');
    // The orders this device kept for them go too (saves still waiting stay theirs).
    forgetOrderCopies(user?.id);
    forgetScribeDrafts(user?.id); // and what they typed for "Write it up"
    // and their unsent inspection notes, order notes and messages (keyed user-<id>-…)
    if (user?.id) clearDraftsStartingWith(`user-${user.id}-`);
    removePref(OFFLINE_ME);
    clearSession();
  };

  // Helper to get auth headers for API calls. Memoized so dependent effects
  // (e.g., LocationContext) don't re-fire on every AuthProvider render.
  const getAuthHeaders = useCallback(() => {
    const token = getAccessToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }, []);

  // Refetch current user + permissions from /auth/me. Call this after changes
  // that affect the current user's own permissions or the company's role defaults,
  // so View as Role and the sidebar reflect the new values without a full reload.
  const refetchCurrentUser = useCallback(async () => {
    const token = getAccessToken();
    if (!token) return false;
    try {
      const response = await fetchAuthMe(token);
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setPermissions(data.permissions || {});
        setAccessibleLocations(data.accessible_locations || []);
        return true;
      }
    } catch (err) {
      console.error('Failed to refetch current user:', err);
    }
    return false;
  }, []);

  const realIsAdmin = user?.role === 'admin';

  // "View as" preview (user + role modes) — extracted to hooks/useViewAsPreview.
  // It arms the X-View-As request headers so the backend authorizes and
  // filters every data call as the previewed target (faithful, read-only).
  const {
    viewAsRole,
    setViewAsRole: setViewAsRoleSafe,
    viewAsUserId,
    viewAsUserData,
    previewAsUser,
    effectiveRole,
    effectivePermissions,
  } = useViewAsPreview({ realIsAdmin, user, permissions, permissionCatalog, permissionDependencies });

  // Permission checks — the core of the new system.
  // can(key) — boolean check. Returns true if effective value is exactly true.
  const can = useCallback((key) => effectivePermissions[key] === true, [effectivePermissions]);
  // is(key, value) — enum check. Returns true if effective value equals value.
  const is = useCallback((key, value) => effectivePermissions[key] === value, [effectivePermissions]);
  // getPermission(key) — returns the effective value (boolean or string)
  const getPermission = useCallback((key) => effectivePermissions[key], [effectivePermissions]);

  // Role checking helpers (based on effective role)
  const hasRole = (role) => effectiveRole === role;
  const isCustomer = effectiveRole === 'customer';
  const isAdmin = effectiveRole === 'admin';
  const isTechnicianManager = effectiveRole === 'technician_manager';
  const isTechnician = effectiveRole === 'technician';
  const isSalesManager = effectiveRole === 'sales_manager';
  const isServiceWriter = effectiveRole === 'service_writer';
  const isSalesSupport = effectiveRole === 'sales_support';

  // Role group helpers
  const isStaff = ['admin', 'technician_manager', 'technician', 'sales_manager', 'service_writer', 'sales_support', 'marketing_manager', 'field_representative', 'event_coordinator', 'creative'].includes(effectiveRole);
  const isManager = ['admin', 'technician_manager', 'sales_manager'].includes(effectiveRole);
  const isTechTeam = ['admin', 'technician_manager', 'technician'].includes(effectiveRole);
  const isSalesTeam = ['admin', 'sales_manager', 'service_writer', 'sales_support'].includes(effectiveRole);

  const value = {
    user,
    loading,
    authRetrying,
    offlineSession,
    error,
    login,
    verify2FA,
    resend2FA,
    register,
    logout,
    refreshToken,
    refetchCurrentUser,
    getAuthHeaders,
    accessibleLocations,
    isAuthenticated: !!user,
    // Permissions (the new system)
    permissions: effectivePermissions,
    rawPermissions: permissions, // the real user's permissions, even during preview
    permissionCatalog, // full catalog for label lookup (e.g., denial toasts)
    can,
    is,
    getPermission,
    // Role + user preview
    effectiveRole,
    viewAsRole,
    setViewAsRole: setViewAsRoleSafe,
    viewAsUserId,
    viewAsUserData,
    previewAsUser,
    realIsAdmin,
    // Individual role checks
    hasRole,
    isCustomer,
    isAdmin,
    isTechnicianManager,
    isTechnician,
    isSalesManager,
    isServiceWriter,
    isSalesSupport,
    // Role group checks
    isStaff,
    isManager,
    isTechTeam,
    isSalesTeam,
    // Legacy alias
    isEmployee: isStaff
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
